import { useMutation } from '@apollo/client';
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useContext, useEffect, useState } from 'react';
import {
  Control,
  FieldValues,
  SubmitHandler,
  UseFormSetValue,
  useForm,
} from 'react-hook-form';
import * as yup from 'yup';
import cms from '../../../apis/cms';
import {
  CREATE_GROUP_ELEMENT,
  REMOVE_GROUP_ELEMENT,
  UPDATE_GROUP_ELEMENT,
} from '../../../graphQL/mutations';
import { GET_PAGE_DATA } from '../../../graphQL/queries';
import RedirectForm, { redirectFormSchema } from '../../FormGroup/RedirectForm';
import Input from '../../Form/Input';
import Upload from '../../Form/Upload';
import context from '../context';

interface Props {
  isOpen: boolean;
  banner?: any;
  onClose: () => void;
  onSaved: () => void;
}

interface FormData {
  name: string;
  file?: FileList;
  redirect_type: { label: string | number; value: string | number };
  redirect_value: string | { label: string | number; value: string | number };
}

const formData = yup
  .object()
  .shape({
    name: yup.string().required('Nome é obrigatório'),
  })
  .concat(redirectFormSchema);

const BannerForm: React.FC<Props> = ({ isOpen, banner, onClose, onSaved }) => {
  const toast = useToast();
  const { pageGroup, display } = useContext(context);

  const {
    formState,
    handleSubmit,
    register,
    control,
    setValue,
    setError,
    reset,
  } = useForm<FormData>({ resolver: yupResolver(formData) });

  const [createGroupElement] = useMutation(CREATE_GROUP_ELEMENT, {
    refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
  });
  const [updateGroupElement] = useMutation(UPDATE_GROUP_ELEMENT, {
    refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
  });
  const [removeGroupElement, { loading: removeLoading }] = useMutation(
    REMOVE_GROUP_ELEMENT,
    { refetchQueries: [GET_PAGE_DATA, 'GetPageData'] },
  );

  const [loading, setLoading] = useState(false);

  const ehEdicao = !!banner?.id;
  const { errors } = formState;

  useEffect(() => {
    reset();
    if (!banner?.id) return;

    setValue('name', banner?.name);
  }, [banner, setValue, reset]);

  const enviarImagem = async (file?: FileList) => {
    if (!file || !file[0]) return undefined;
    const multipart = new FormData();
    multipart.append('file', file[0]);
    const { data } = await cms.rest.post<any>('/upload', multipart);
    return data?.id as string;
  };

  const handleForm: SubmitHandler<FormData> = async data => {
    const { file, ...rest } = data;

    if (!ehEdicao && !file?.length) {
      setError('file', { type: 'manual', message: 'Insira uma imagem' });
      return;
    }

    try {
      setLoading(true);
      const file_id = await enviarImagem(file);

      const dadosDoItem = {
        name: rest.name,
        redirect_type: rest.redirect_type?.value,
        redirect_value:
          typeof rest.redirect_value === 'object'
            ? rest.redirect_value?.value
            : rest.redirect_value,
        ...(file_id ? { file_id } : {}),
      };

      if (ehEdicao) {
        await updateGroupElement({
          variables: {
            updateGroupElementInput: { ...dadosDoItem, id: banner.id },
          },
        });
      } else {
        await createGroupElement({
          variables: {
            createGroupElementInput: {
              ...dadosDoItem,
              page_group_id: pageGroup?.id,
              element_id: display?.element_id,
              position: pageGroup?.items?.length ?? 0,
            },
          },
        });
      }

      toast({
        title: 'Sucesso!',
        description: ehEdicao ? 'Banner atualizado!' : 'Banner criado!',
        status: 'success',
        position: 'top-right',
      });
      onSaved();
    } catch (err: any) {
      toast({
        title: 'Opss!!',
        description:
          err?.message || 'Tivemos um erro, tente novamente mais tarde',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const haBannersDepois =
      banner.position < (pageGroup?.items?.length ?? 0) - 1;
    const aviso = haBannersDepois
      ? 'Os banners seguintes vão subir uma posição. Tem certeza que deseja excluir este banner?'
      : 'Tem certeza que deseja excluir este banner?';
    if (!window.confirm(aviso)) return;
    try {
      await removeGroupElement({ variables: { id: banner.id } });
      toast({
        title: 'Sucesso!',
        description: 'Banner removido',
        status: 'success',
        position: 'top-right',
      });
      onSaved();
    } catch (err: any) {
      toast({
        title: 'Opss!!',
        description: err?.message || 'Não foi possível remover',
        status: 'error',
        position: 'top-right',
      });
    }
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="md" placement="right">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader>
          {ehEdicao ? 'Editar banner' : 'Novo banner'}
        </DrawerHeader>
        <DrawerBody>
          <VStack as="form" spacing="6" onSubmit={handleSubmit(handleForm)}>
            {/* o Upload desenha o rótulo acima da caixa de 150px e transborda; mb compensa */}
            <Upload
              mb={10}
              label="Imagem"
              alignSelf="center"
              error={errors?.file as any}
              url={banner?.file?.url}
              {...register('file')}
            />

            <Input
              isRequired
              label="Nome"
              placeholder="Ex: Banner Black November"
              error={errors?.name}
              {...register('name')}
            />

            {/* RedirectForm é tipado com FieldValues; os casts evitam @ts-nocheck no arquivo */}
            <RedirectForm
              control={control as unknown as Control<FieldValues>}
              formState={formState}
              register={register}
              setValue={setValue as unknown as UseFormSetValue<FieldValues>}
              pageElement={banner}
              mostrarFacets={false}
            />
          </VStack>
        </DrawerBody>

        <DrawerFooter>
          <HStack spacing={4}>
            {ehEdicao && (
              <Button
                colorScheme="red"
                isLoading={removeLoading}
                onClick={handleDelete}
              >
                Excluir
              </Button>
            )}
            <Button onClick={onClose}>Fechar</Button>
            <Button
              colorScheme="brand"
              isLoading={loading}
              onClick={handleSubmit(handleForm)}
            >
              Salvar
            </Button>
          </HStack>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default BannerForm;
